<div class="space-y-4" id="prompt_history_panel">

  <div
    *ngIf="errorMsg()"
    class="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-xs text-red-700 dark:text-red-400"
    id="prompt_history_error"
  >
    {{ errorMsg() }}
  </div>

  <!-- Loading skeleton -->
  <div *ngIf="loading()" class="space-y-2">
    <div *ngFor="let i of [1,2,3]" class="h-12 rounded-xl bg-slate-100 dark:bg-slate-800/50 animate-pulse"></div>
  </div>

  <p *ngIf="!loading() && !versions().length" class="py-10 text-center text-slate-400 dark:text-slate-500 text-xs">
    No versions recorded yet — save a change to start the history.
  </p>

  <!-- Version list, newest first. Immutable: view + restore only, never edit/delete. -->
  <div *ngIf="versions().length" class="rounded-xl border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700/50">
    <div
      *ngFor="let version of versions(); trackBy: trackByVersion"
      class="flex items-start gap-3 px-3 py-2.5 hover:bg-slate-50/80 dark:hover:bg-slate-700/20 transition-colors"
      [attr.id]="'prompt_version_row_' + version.version"
    >
      <span
        class="shrink-0 mt-0.5 px-2 py-0.5 rounded-md text-[10px] font-bold font-mono"
        [ngClass]="version.version === currentVersion()
          ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400'
          : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'"
      >
        v{{ version.version }}
      </span>

      <div class="flex-1 min-w-0">
        <p class="text-xs font-medium text-slate-700 dark:text-slate-200 leading-snug">
          {{ version.changeNotes }}
          <span *ngIf="version.version === currentVersion()" class="ml-1 text-[10px] font-semibold text-emerald-500">· current</span>
        </p>
        <p class="text-[10px] text-slate-400 mt-0.5">
          {{ version.name }} · {{ version.promptChars | number }} chars
          <ng-container *ngIf="version.createdBy"> · {{ version.createdBy }}</ng-container>
          <ng-container *ngIf="version.createdAt"> · {{ version.createdAt }}</ng-container>
        </p>
      </div>

      <div class="flex items-center gap-1 shrink-0">
        <button
          *ngIf="version.version !== currentVersion()"
          (click)="viewDiff(version)"
          class="px-2 py-1 rounded-lg text-[10px] font-semibold transition-colors"
          [ngClass]="selectedVersion()?.version === version.version
            ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300'
            : 'text-slate-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-white/10'"
          [attr.id]="'prompt_version_diff_btn_' + version.version"
        >
          {{ selectedVersion()?.version === version.version ? 'Hide diff' : 'View diff' }}
        </button>
        <button
          *ngIf="version.version !== currentVersion()"
          (click)="confirmRestore(version)"
          class="px-2 py-1 rounded-lg text-[10px] font-semibold text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors"
          [attr.id]="'prompt_version_restore_btn_' + version.version"
        >
          Restore
        </button>
      </div>
    </div>
  </div>

  <!-- Side-by-side diff: selected old version vs the current text -->
  <div *ngIf="loadingDiff()" class="flex items-center justify-center py-6">
    <span class="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-300">
      <span class="w-4 h-4 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin"></span>
      Loading versions to compare…
    </span>
  </div>

  <app-prompt-diff
    *ngIf="selectedVersion() && !loadingDiff()"
    [oldText]="selectedVersion()!.systemPrompt ?? ''"
    [newText]="currentText()"
    [oldLabel]="'v' + selectedVersion()!.version + ' — ' + selectedVersion()!.changeNotes"
    [newLabel]="'v' + currentVersion() + ' (current)'"
  ></app-prompt-diff>
</div>

<!-- Restore confirmation -->
<div
  *ngIf="restoreTarget()"
  class="fixed inset-0 z-[9100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
  id="prompt_version_restore_modal"
>
  <div class="bg-white dark:bg-surface-1 rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4" (click)="$event.stopPropagation()">
    <div class="flex items-center gap-3 mb-4">
      <div class="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
        <svg class="w-5 h-5 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="1 4 1 10 7 10"></polyline>
          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
        </svg>
      </div>
      <h3 class="text-sm font-bold text-slate-800 dark:text-white">Restore v{{ restoreTarget()!.version }}?</h3>
    </div>

    <p class="text-xs text-slate-600 dark:text-slate-300 mb-5">
      The v{{ restoreTarget()!.version }} snapshot becomes a <strong>new version</strong> and the
      assistant runs with it. Nothing is deleted — the history keeps every version.
    </p>

    <div class="flex gap-3">
      <button (click)="cancelRestore()"
              class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
              id="prompt_version_restore_cancel_btn">Cancel</button>
      <button (click)="executeRestore()" [disabled]="restoring()"
              class="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 transition-colors"
              id="prompt_version_restore_confirm_btn">
        <span *ngIf="!restoring()">Restore</span>
        <span *ngIf="restoring()" class="flex items-center justify-center gap-2">
          <span class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
          Restoring…
        </span>
      </button>
    </div>
  </div>
</div>
